import { type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { DownOutlined } from '@ant-design/icons';
import { VIEW_GROUPS } from '../viewFiltering/viewTaxonomy';
import styles from './ViewGroupsFilter.module.css';

interface ViewGroupsFilterProps {
  /** Selected group id, or null for "All views". */
  value: string | null;
  onChange: (id: string | null) => void;
}

const LISTBOX_ID = 'view-group-listbox';
const optionDomId = (index: number) => `view-group-option-${index}`;

interface GroupOption {
  id: string | null;
  label: string;
}

const OPTIONS: GroupOption[] = [
  { id: null, label: 'All views' },
  ...VIEW_GROUPS.map((g) => ({ id: g.id, label: g.displayName })),
];

/**
 * Single-select for clinical View Groups (AP-441). Picking a group filters the
 * study to that group's views; "All views" clears it. Implements the listbox
 * keyboard contract (Arrow/Enter/Escape) with aria-activedescendant.
 */
export function ViewGroupsFilter({ value, onChange }: ViewGroupsFilterProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);

  const selectedIndex = OPTIONS.findIndex((o) => o.id === value);
  const selected = selectedIndex > 0 ? OPTIONS[selectedIndex] : null;

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const openMenu = () => {
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setOpen(true);
  };

  const choose = (id: string | null) => {
    onChange(id);
    setOpen(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) openMenu();
      else setActiveIndex((i) => Math.min(i + 1, OPTIONS.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) openMenu();
      else setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (open && OPTIONS[activeIndex]) choose(OPTIONS[activeIndex].id);
      else openMenu();
    } else if (e.key === 'Escape' && open) {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <span className={styles.label} id="view-group-label">
        View Group
      </span>
      <button
        type="button"
        role="combobox"
        className={styles.field}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? LISTBOX_ID : undefined}
        aria-activedescendant={open ? optionDomId(activeIndex) : undefined}
        aria-labelledby="view-group-label"
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={handleKeyDown}
        data-testid="view-group-trigger"
      >
        <span className={selected ? styles.value : styles.placeholder}>
          {selected ? selected.label : 'All views'}
        </span>
        <DownOutlined aria-hidden className={styles.chevron} />
      </button>

      {open && (
        <ul
          className={styles.popover}
          role="listbox"
          id={LISTBOX_ID}
          aria-label="View groups"
        >
          {OPTIONS.map((opt, i) => (
            <li
              key={opt.id ?? '__all__'}
              id={optionDomId(i)}
              role="option"
              aria-selected={opt.id === value}
              className={`${styles.option} ${
                i === activeIndex ? styles.optionActive : ''
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                choose(opt.id);
              }}
              onMouseEnter={() => setActiveIndex(i)}
              data-testid={
                opt.id === null ? 'view-group-all' : `view-group-${opt.id}`
              }
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
